import { z } from "zod";

/**
 * Browser onboarding validation (Phase 4).
 *
 * Boundary validation for the Microsoft 365 Onboarding launch flow. Both the
 * API request body and the destination URL allow-list live here so the web and
 * API sides validate identically. The API remains authoritative.
 */

/**
 * The ONLY hosts a launch request may target. Exact hostname match (lowercased),
 * HTTPS only. Anything else — http/file/data/javascript/ftp schemes, localhost,
 * loopback IPs, UNC paths, local file paths, or any other host — is rejected.
 */
export const ALLOWED_DESTINATION_HOSTS = [
  "admin.microsoft.com",
  "login.microsoftonline.com",
  "entra.microsoft.com",
  "portal.azure.com",
  "partner.microsoft.com",
] as const;

export type AllowedDestinationHost = (typeof ALLOWED_DESTINATION_HOSTS)[number];

/** The default Microsoft partner indirect-reseller invitation destination. */
export const DEFAULT_INVITATION_URL =
  "https://admin.microsoft.com/Adminportal/Home?invType=IndirectResellerRelationship&partnerId=694cc485-72c9-4991-9fe3-12f8becdc5c2&msppId=528893#/BillingAccounts/partner-invitation";

/** Neutral Microsoft 365 landing page used when opening an isolated session. */
export const DEFAULT_M365_ADMIN_URL = "https://admin.microsoft.com/";

/** How long a launch request stays valid before the agent must redeem it. */
export const LAUNCH_REQUEST_TTL_MS = 5 * 60_000;

export interface DestinationValidationError {
  reason: string;
}

export type DestinationValidationResult =
  | { ok: true; url: string; host: AllowedDestinationHost }
  | { ok: false; reason: string };

/**
 * Validates and normalizes a destination URL against the security policy.
 * Returns a discriminated result instead of throwing so callers can map it to
 * either a Zod issue (request validation) or an HTTP error (service layer).
 */
export function validateDestinationUrl(raw: string): DestinationValidationResult {
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return { ok: false, reason: "Destination URL is not a valid absolute URL" };
  }

  // HTTPS only — rejects http:, file:, data:, javascript:, ftp:, etc.
  if (parsed.protocol !== "https:") {
    return { ok: false, reason: "Destination URL must use https://" };
  }

  // Reject embedded credentials (https://user:pass@host).
  if (parsed.username || parsed.password) {
    return { ok: false, reason: "Destination URL must not contain credentials" };
  }

  const host = parsed.hostname.toLowerCase();

  // Defensive: explicitly reject loopback even though it can't be in the list.
  if (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "::1" ||
    host === "[::1]"
  ) {
    return { ok: false, reason: "Local hosts are not permitted" };
  }

  if (!(ALLOWED_DESTINATION_HOSTS as readonly string[]).includes(host)) {
    return {
      ok: false,
      reason: `Destination host "${host}" is not in the approved Microsoft list`,
    };
  }

  return { ok: true, url: parsed.toString(), host: host as AllowedDestinationHost };
}

/**
 * Zod schema for POST /api/v1/browser-sessions/launch-request. The destination
 * URL is validated with the allow-list above; failures surface as a field-level
 * issue on `destinationUrl`.
 */
export const createBrowserLaunchRequestSchema = z.object({
  customerName: z
    .string()
    .trim()
    .min(1, "Customer name is required")
    .max(200, "Customer name is too long"),
  customerId: z
    .string()
    .trim()
    .max(100, "Customer ID is too long")
    .optional()
    // Normalize empty strings from the form to undefined.
    .transform((v) => (v && v.length > 0 ? v : undefined)),
  destinationUrl: z
    .string()
    .trim()
    .min(1, "Destination URL is required")
    .superRefine((value, ctx) => {
      const result = validateDestinationUrl(value);
      if (!result.ok) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: result.reason });
      }
    }),
});

export type CreateBrowserLaunchRequestInput = z.infer<
  typeof createBrowserLaunchRequestSchema
>;

/**
 * Schema for the identifiers carried in the `yazamcoops://launch-browser` URL.
 *
 * The agent applies this before it calls back. It is a shape check only, and is
 * not meant to be more: it proves nothing about authenticity. The API's atomic
 * nonce redemption is the actual control — this just stops a malformed URL from
 * turning into a pointless API round-trip.
 */
export const browserLaunchTicketSchema = z.object({
  requestId: z.string().uuid(),
  sessionId: z.string().uuid(),
  nonce: z.string().min(16).max(200),
  token: z.string().min(20).max(200),
});

/** Dedicated Browser Agent callback payloads. */
export const browserAgentEventSchema = z.object({
  agentVersion: z.string().trim().min(1).max(50),
  workstationId: z.string().trim().min(1).max(200),
  errorCode: z.string().trim().min(1).max(100).optional(),
  cleanupStatus: z.enum(["pending", "success", "failed"]).optional(),
});

export const consumeBrowserLaunchSchema = browserAgentEventSchema.extend({
  requestId: z.string().uuid(),
  nonce: z.string().min(16).max(200),
});
